import { useState } from "react";
import Spinner from "./Spinner";
import styles from "../../PricingTool.module.css";

export interface ComboOption {
  key: string;
  label: string;
  sub?: string;
  header?: boolean;
  indent?: boolean;
}

interface Props {
  displayText: string;
  options: ComboOption[];
  selectedKey?: string;
  onPick: (k: string) => void;
  placeholder: string;
  onSearch?: (q: string) => void;
  loading?: boolean;
}

export default function Combo({
  displayText,
  options,
  selectedKey,
  onPick,
  placeholder,
  onSearch,
  loading: searchLoading,
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();
  const list = onSearch
    ? options
    : options.filter((o) =>
        (o.label + " " + (o.sub || "")).toLowerCase().includes(q)
      );

  const handleSearchChange = (val: string) => {
    setSearch(val);
    if (onSearch) onSearch(val);
  };

  return (
    <div className={styles.combo_wrapper}>
      {open && (
        <div className={styles.combo_overlay} onClick={() => setOpen(false)} />
      )}
      <div
        className={`${styles.combo_trigger} ${open ? styles.combo_trigger_open : ""}`}
        onClick={() => {
          setOpen(true);
          setSearch("");
          if (onSearch) onSearch("");
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(0,0,0,0.35)"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        {open ? (
          <input
            autoFocus
            className={styles.combo_search_input}
            value={search}
            placeholder={placeholder}
            onChange={(e) => handleSearchChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={styles.combo_trigger_text}>{displayText}</span>
        )}
        {searchLoading && open ? (
          <Spinner size={14} />
        ) : (
          <span className={styles.combo_trigger_caret}>▾</span>
        )}
      </div>
      {open && (
        <div className={styles.combo_dropdown}>
          {searchLoading ? (
            <div
              style={{
                padding: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "#6B4EFF",
                fontSize: 12,
              }}
            >
              <Spinner size={14} /> Searching…
            </div>
          ) : list.length === 0 ? (
            <div
              style={{ padding: 10, fontSize: 12, color: "rgba(0,0,0,0.4)" }}
            >
              {onSearch && !search ? "Start typing to search" : "No matches"}
            </div>
          ) : (
            list.map((o) => (
              <button
                key={o.key}
                className={`${styles.combo_option} ${o.header ? styles.combo_option_header : ""} ${o.indent ? styles.combo_option_indent : ""} ${o.key === selectedKey ? styles.combo_option_selected : ""}`}
                onClick={() => {
                  onPick(o.key);
                  setOpen(false);
                }}
              >
                <span
                  style={{
                    fontSize: o.header ? 11 : 13,
                    fontWeight: o.header ? 700 : 500,
                    color: o.header ? "#6B4EFF" : "rgba(0,0,0,0.88)",
                    textTransform: o.header
                      ? ("uppercase" as const)
                      : undefined,
                    letterSpacing: o.header ? "0.03em" : undefined,
                  }}
                >
                  {o.label}
                </span>
                {o.sub && (
                  <span
                    style={{
                      fontSize: 10,
                      color: o.header ? "#6B4EFF" : "rgba(0,0,0,0.45)",
                      marginTop: o.header ? 0 : 2,
                      lineHeight: "1.3",
                      fontWeight: o.header ? 600 : 400,
                      opacity: o.header ? 0.7 : 1,
                    }}
                  >
                    {o.sub}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
